// ---------------------------------------------------------------------------
// Shared time-based conflict utilities for the venue booking system.
//
// Used by:
//   - /api/venue-bookings/availability (availability checks)
//   - /api/venue-bookings (submission conflict guard)
//   - /api/venue-bookings/action (approval conflict guard)
//   - TimeGrid.tsx (client-side display)
// ---------------------------------------------------------------------------

import type { Venue } from "@/lib/venues";

/** Minutes of "grace period" added after every booking end. */
export const GRACE_MINUTES = 60;

/**
 * Returns true when a proposed booking [newStart, newEnd) conflicts with an
 * existing booking [existingStart, existingEnd), factoring in the grace period
 * that applies after BOTH bookings (minimum turnaround gap).
 *
 * Conflict condition:
 *   new starts before (existing end + grace) AND
 *   existing starts before (new end + grace)
 */
export function hasTimeConflict(
  newStart: Date,
  newEnd: Date,
  existingStart: Date,
  existingEnd: Date,
  graceMinutes = GRACE_MINUTES,
): boolean {
  const graceMs = graceMinutes * 60_000;
  const newBlockedEnd = newEnd.getTime() + graceMs;
  const existingBlockedEnd = existingEnd.getTime() + graceMs;
  return (
    newStart.getTime() < existingBlockedEnd &&
    existingStart.getTime() < newBlockedEnd
  );
}

/**
 * Given a venue and a set of requested space IDs, returns every space ID that
 * must be checked for conflicts — i.e., the requested spaces themselves PLUS
 * any spaces listed in their `conflictsWith` arrays.
 *
 * Example: booking "court-1" on Legacy Hall also needs to check "full-hall"
 * because full-hall.conflictsWith includes "court-1".
 */
export function getConflictSpaceIds(venue: Venue, requestedSpaceIds: string[]): string[] {
  if (!venue.spaces) return requestedSpaceIds;
  const result = new Set<string>(requestedSpaceIds);

  // Forward relationships: requested -> conflictsWith
  for (const spaceId of requestedSpaceIds) {
    const space = venue.spaces.find((s) => s.id === spaceId);
    if (space?.conflictsWith) {
      for (const id of space.conflictsWith) result.add(id);
    }
  }

  // Reverse relationships: any space that lists a requested space as a conflict.
  // This keeps conflict checks correct even if config is not perfectly symmetrical.
  for (const space of venue.spaces) {
    if (!space.conflictsWith?.length) continue;
    if (space.conflictsWith.some((id) => requestedSpaceIds.includes(id))) {
      result.add(space.id);
    }
  }

  return [...result];
}

// ---------------------------------------------------------------------------
// HH:mm helpers used by both the API and the client time-grid component.
// ---------------------------------------------------------------------------

/** Converts "HH:mm" to total minutes from midnight. */
export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + (m ?? 0);
}

/** Converts total minutes from midnight to "HH:mm". */
export function fromMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Generates every 30-minute time point from `start` to `end` (both "HH:mm"),
 * inclusive. E.g. ("07:00", "21:00") → ["07:00", "07:30", ..., "21:00"].
 */
export function generateTimePoints(start: string, end: string): string[] {
  const startM = toMinutes(start);
  const endM = toMinutes(end);
  const points: string[] = [];
  for (let m = startM; m <= endM; m += 30) points.push(fromMinutes(m));
  return points;
}

export type SlotStatus = "available" | "blocked" | "grace";

/**
 * Computes the status of a 30-minute segment [segStart, segEnd] against a set
 * of blocked ranges. All arguments are "HH:mm" strings.
 *
 * - "blocked" — the segment overlaps with a booking
 * - "grace"   — the segment falls within GRACE_MINUTES before booking start
 *               or after booking end
 * - "available" — neither
 */
export function getSegmentStatus(
  segStart: string,
  segEnd: string,
  blockedRanges: { start: string; end: string }[],
  graceMinutes = GRACE_MINUTES,
): SlotStatus {
  const ssM = toMinutes(segStart);
  const seM = toMinutes(segEnd);

  for (const r of blockedRanges) {
    const rS = toMinutes(r.start);
    const rE = toMinutes(r.end);
    const graceS = rS - graceMinutes;
    const graceE = rE + graceMinutes;

    // Segment overlaps with the booking itself?
    if (rS < seM && rE > ssM) return "blocked";

    // Segment starts within the pre-booking grace window?
    if (ssM >= graceS && ssM < rS) return "grace";

    // Segment start falls within the grace window?
    if (ssM >= rE && ssM < graceE) return "grace";
  }
  return "available";
}

/**
 * Returns true when the proposed time range [start, end] (both "HH:mm") is
 * entirely clear of blocked and grace segments.
 */
export function isRangeAvailable(
  start: string,
  end: string,
  blockedRanges: { start: string; end: string }[],
  graceMinutes = GRACE_MINUTES,
): boolean {
  const points = generateTimePoints(start, end);
  // Check every segment within the range
  for (let i = 0; i < points.length - 1; i++) {
    if (getSegmentStatus(points[i], points[i + 1], blockedRanges, graceMinutes) !== "available") {
      return false;
    }
  }
  return true;
}
